import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { USER_ROLES } from '@hyperke/shared';
import { message } from 'ant-design-vue';

/**
 * Route definitions
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/client-feedback/appointments/:token',
    name: 'ClientFeedbackAppointments',
    component: () => import('@/views/client-feedback/Appointments.vue'),
    meta: { requiresAuth: false, public: true },
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: () => import('@/views/ChangePassword.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/',
    redirect: '/leads',
  },
  {
    path: '/leads',
    name: 'Leads',
    component: () => import('@/views/Leads.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/appointments',
    name: 'Appointments',
    component: () => import('@/views/Appointments.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/client-feedback-links',
    name: 'ViewClientFeedbackLinks',
    component: () => import('@/views/ViewClientFeedbackLinks.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/client-leads-tokens',
    name: 'ClientLeadsTokens',
    component: () => import('@/views/admin/ClientLeadsTokens.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/users',
    name: 'UserManagement',
    component: () => import('@/views/admin/UserManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/client-leads/:client_code/:token',
    name: 'ClientLeadsPortal',
    component: () => import('@/views/ClientLeadsPortal.vue'),
    meta: { requiresAuth: false, public: true },
  },
  // ============================================
  // EMPLOYEE INCENTIVES ROUTES
  // ============================================
  {
    path: '/incentives/my-performance',
    name: 'MyIncentives',
    component: () => import('@/views/incentives/MyPerformance.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/im-analytics',
    name: 'MyPerformance',
    component: () => import('@/views/MyPerformance.vue'),
    meta: { requiresAuth: true },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;

      // Admins should access the admin analytics view instead
      if (user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.ADMIN_ASSISTANT) {
        message.info('Redirecting to full admin analytics...');
        next({ name: 'IMAnalytics', replace: true });
      } else {
        next();
      }
    },
  },
  {
    path: '/admin/audit-logs',
    name: 'AuditLogs',
    component: () => import('@/views/admin/AuditLogs.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/client-feedback-links',
    name: 'ClientFeedbackLinks',
    component: () => import('@/views/admin/ClientFeedbackLinks.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/im-analytics',
    name: 'IMAnalytics',
    component: () => import('@/views/admin/IMAnalytics.vue'),
    meta: { requiresAuth: true },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;

      // IMs should access their personal analytics view instead
      if (user?.role === USER_ROLES.IM) {
        message.info('Redirecting to your personal analytics...');
        next({ name: 'MyPerformance', replace: true });
      } else {
        next();
      }
    },
  },
  {
    path: '/admin/sdr-performance',
    name: 'SDRAnalytics',
    component: () => import('@/views/admin/SDRAnalytics.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;
      if (user?.role === USER_ROLES.SDR) {
        message.info('Redirecting to your SDR performance...');
        next({ name: 'MySDRPerformance', replace: true });
      } else {
        next();
      }
    },
  },
  {
    path: '/my-sdr-performance',
    name: 'MySDRPerformance',
    component: () => import('@/views/MySDRPerformance.vue'),
    meta: { requiresAuth: true },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore();
      const user = authStore.user;
      if (user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.ADMIN_ASSISTANT) {
        next({ name: 'SDRAnalytics', replace: true });
      } else {
        next();
      }
    },
  },
  {
    path: '/admin/im-work-schedules',
    name: 'IMWorkSchedules',
    component: () => import('@/views/admin/IMWorkSchedules.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/active-impersonations',
    name: 'ActiveImpersonations',
    component: () => import('@/views/admin/ActiveImpersonations.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  // ============================================
  // INCENTIVES ADMIN ROUTES
  // ============================================
  {
    path: '/admin/incentives',
    name: 'IncentivesAdmin',
    redirect: '/admin/incentives/periods',
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/incentives/periods',
    name: 'IncentivesPeriods',
    component: () => import('@/views/admin/incentives/PeriodManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/incentives/configs',
    name: 'IncentivesConfigs',
    component: () => import('@/views/admin/incentives/ClientConfigManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/incentives/payouts',
    name: 'IncentivesPayouts',
    component: () => import('@/views/admin/incentives/PayoutManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/incentives/audit-log',
    name: 'IncentivesAuditLog',
    component: () => import('@/views/admin/incentives/IncentivesAuditLog.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
];

/**
 * Vue Router instance with auth guard
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Auth guard
// P0.2 Fix: Enhanced route guard to prevent access to protected pages after logout
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();

  // Check authentication from multiple sources for reliability
  const storeToken = authStore.token;
  const storeUser = authStore.user;
  const localStorageToken = localStorage.getItem('token');
  const localStorageUser = localStorage.getItem('user');

  // DIAGNOSTIC: Log detailed state before any checks
  console.log('[Router Guard] AUTH STATE CHECK', {
    route: { to: to.name, from: from.name },
    store: {
      hasToken: !!storeToken,
      tokenPrefix: storeToken ? storeToken.substring(0, 20) + '...' : 'none',
      hasUser: !!storeUser,
      userRole: storeUser?.role,
      userEmail: storeUser?.email,
    },
    localStorage: {
      hasToken: !!localStorageToken,
      tokenPrefix: localStorageToken ? localStorageToken.substring(0, 20) + '...' : 'none',
      hasUser: !!localStorageUser,
    },
    comparison: {
      tokensMatch: storeToken === localStorageToken,
      usersMatch: JSON.stringify(storeUser) === localStorageUser,
      tokenMismatch: (storeToken && !localStorageToken) || (!storeToken && localStorageToken),
    }
  });

  // User is authenticated only if BOTH store and localStorage have valid token/user
  const isAuthenticated = !!(storeToken && storeUser && localStorageToken && localStorageUser);

  // If store and localStorage are out of sync, clear both (security measure)
  if ((storeToken && !localStorageToken) || (!storeToken && localStorageToken)) {
    const debugInfo = {
      timestamp: new Date().toISOString(),
      type: 'router_guard_token_mismatch',
      route: { to: to.name, from: from.name, fullPath: to.fullPath },
      storeState: {
        hasToken: !!storeToken,
        tokenPrefix: storeToken ? storeToken.substring(0, 20) + '...' : 'none',
        hasUser: !!storeUser,
        userRole: storeUser?.role,
        userEmail: storeUser?.email,
      },
      localStorageState: {
        hasToken: !!localStorageToken,
        tokenPrefix: localStorageToken ? localStorageToken.substring(0, 20) + '...' : 'none',
        hasUser: !!localStorageUser,
      },
      mismatchType: storeToken && !localStorageToken ? 'store_exists_localstorage_missing' : 'localstorage_exists_store_missing',
    };

    // Write to sessionStorage for persistent debugging
    sessionStorage.setItem('__hyperke_router_guard_debug__', JSON.stringify(debugInfo));

    console.warn('[Router Guard] Token mismatch - persistent log written to __hyperke_router_guard_debug__', {
      storeToken: !!storeToken,
      localStorageToken: !!localStorageToken,
      mismatchType: storeToken && !localStorageToken ? 'store exists, localStorage missing' : 'localStorage exists, store missing',
      route: to.name,
    });

    authStore.logout();
    next({ name: 'Login', query: { redirect: to.fullPath } });
    return;
  }
  
  // Check must_change_password from both store and localStorage (for reliability)
  const storeMustChange = authStore.user?.must_change_password === true;
  let localStorageMustChange = false;
  try {
    if (localStorageUser) {
      const parsedUser = JSON.parse(localStorageUser);
      localStorageMustChange = parsedUser?.must_change_password === true;
    }
  } catch (e) {
    // Ignore parse errors
  }
  
  const mustChangePassword = storeMustChange || localStorageMustChange;

  console.log('[Router Guard]', {
    to: to.name,
    from: from.name,
    isAuthenticated,
    hasStoreToken: !!storeToken,
    hasLocalStorageToken: !!localStorageToken,
    storeMustChange,
    localStorageMustChange,
    mustChangePassword,
  });

  // P0.2 Fix: If route requires auth but user is not authenticated, redirect to login
  // Exception: Public routes (like client feedback) don't require auth
  if (to.meta.requiresAuth && !isAuthenticated && !to.meta.public) {
    console.log('[Router Guard] Protected route requires auth, redirecting to login');
    next({ name: 'Login', query: { redirect: to.fullPath } });
    return;
  }

  // Check if route requires admin role (ADMIN or ADMIN_ASSISTANT)
  if (to.meta.requiresAdmin && isAuthenticated) {
    const userRole = authStore.user?.role;
    if (userRole !== 'ADMIN' && userRole !== 'ADMIN_ASSISTANT') {
      console.log('[Router Guard] Admin route accessed by non-admin user');
      next({ name: 'Leads' }); // Redirect to leads if not admin
      return;
    }
  }
  
  // If user is already logged in and tries to access login page, redirect to leads
  if (to.name === 'Login' && isAuthenticated) {
    // If user must change password, redirect to change password page
    if (mustChangePassword) {
      console.log('[Router Guard] Redirecting to ChangePassword from Login');
      next({ name: 'ChangePassword', replace: true });
    } else {
      next({ name: 'Leads', replace: true });
    }
    return;
  }
  
  // Force redirect to change password if user must change password
  if (to.name !== 'ChangePassword' && isAuthenticated && mustChangePassword) {
    console.log('[Router Guard] Force redirecting to ChangePassword');
    next({ name: 'ChangePassword', replace: true });
    return;
  }
  
  next();
});

export default router;